import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMe } from '../hooks/useMe'
import { FEATURE_TIER, type PlanFeatureFlag } from '../lib/plans'

// Flag is the shared entitlement-flag union (sourced from lib/plans so the
// gate and the Billing grid can't drift). Each flag is a boolean key on the
// Subscription that the allow logic below reads.
type Flag = PlanFeatureFlag

// SubscriptionGate wraps a control and tells the caller whether the
// owner is entitled to use it. Use the render-prop form so the gated
// control can render itself disabled (e.g. an <Input disabled>) without
// the gate needing to know its props. The reason text appears as a
// tooltip below the control when locked.
//
// Default-locked (FE7): the control is treated as locked until /v1/me
// has *successfully* loaded and reports the flag granted. While the
// profile is loading — or if the request errored — the gate stays
// locked, so a paid control never flashes interactable before we know
// the owner is entitled. The server is still the hard boundary; this
// only keeps the UI from getting ahead of it.
//
// FE-3: a /v1/me *error* is treated distinctly from a genuine "not on
// this plan" — the control stays locked (fail-safe), but the helper
// text shows a neutral "couldn't load your plan — Retry" instead of an
// upsell, so a transient failure never mis-sells to an entitled user.
export function SubscriptionGate({
  flag,
  children,
  upsellMessage,
}: {
  flag: Flag
  children: (state: { locked: boolean; loading: boolean; reason: string | null }) => ReactNode
  upsellMessage?: string
}) {
  const me = useMe()
  // allowed is false until the profile loads successfully AND grants the
  // flag. isLoading and any error both leave allowed false → locked, so a
  // paid control never flashes interactable before we know the owner is
  // entitled.
  const allowed = me.isSuccess && !!me.data?.subscription?.[flag]
  const locked = !allowed
  const loading = me.isLoading
  // FE-3: a /v1/me failure is NOT an entitlement decision — distinguish it
  // from a genuine "not on this plan" so a paid user who hit a transient
  // error sees a neutral retry rather than a mis-targeted upsell.
  const errored = me.isError
  const reason =
    locked && !loading && !errored ? upsellMessage ?? defaultReason(flag) : null
  return (
    <div>
      {children({ locked, loading, reason })}
      {loading ? (
        <p className="mt-1 text-xs text-muted">Checking your plan…</p>
      ) : errored ? (
        <p className="mt-1 text-xs text-muted">
          Couldn't load your plan.{' '}
          <button
            type="button"
            onClick={() => me.refetch()}
            className="font-medium text-glow-600 hover:underline"
          >
            Retry
          </button>
        </p>
      ) : (
        locked && (
          <p className="mt-1 text-xs text-muted">
            {reason}{' '}
            <Link to="/billing" className="font-medium text-glow-600 hover:underline">
              Upgrade
            </Link>
          </p>
        )
      )}
    </div>
  )
}

// defaultReason names the LOWEST tier that unlocks the gated feature, via
// the shared FEATURE_TIER map (cumulative, D1). Each case keeps a
// feature-specific lead-in so the tooltip reads naturally.
function defaultReason(flag: Flag): string {
  const tier = FEATURE_TIER[flag]
  switch (flag) {
    case 'can_set_public':
      return `Public glows require the ${tier} plan.`
    case 'can_add_emails':
      return `Adding email addresses requires the ${tier} plan.`
    case 'can_add_patterns':
      return `Adding email patterns requires the ${tier} plan.`
    case 'can_choose_name':
      return `Choosing a custom name requires the ${tier} plan.`
    case 'can_get_cname':
      return `Custom CNAMEs require the ${tier} plan.`
    case 'can_set_accounts':
      return `Open self-serve accounts require the ${tier} plan.`
    case 'can_hide_branding':
      return `Removing Backlit branding requires the ${tier} plan.`
  }
}
